// Suspends the drill when the app leaves the foreground and restores the mic
// toggle when it returns. blur/focus catch app switches that some compositors
// never report via visibilitychange; the handlers are idempotent, so
// double-firing is harmless. Nothing is restored on pageshow: iOS requires a
// user gesture (the Play tap) to restart capture.

import * as micStream from './micStream';
import { releaseAudioContext } from './passSound';
import { ACTIVE_STATUSES } from './drill';

export default class BackgroundGuard {
  /**
   * @param {Object} host store, controller (DrillController), getDrill, send,
   *   onPageHide (runs before the suspend)
   */
  constructor (host) {
    this.host = host;
    this._micWasOnBeforeSuspend = false;
    this.resume = this.resume.bind(this);
    this._onBlur = () => this.suspend();
    this._onVisibility = () => (document.hidden ? this.suspend({ releaseAudio: true }) : this.resume());
    this._onPageHide = () => {
      host.onPageHide();
      this.suspend({ releaseAudio: true });
    };
  }

  attach () {
    document.addEventListener('visibilitychange', this._onVisibility);
    window.addEventListener('pagehide', this._onPageHide);
    window.addEventListener('blur', this._onBlur);
    window.addEventListener('focus', this.resume);
  }

  detach () {
    document.removeEventListener('visibilitychange', this._onVisibility);
    window.removeEventListener('pagehide', this._onPageHide);
    window.removeEventListener('blur', this._onBlur);
    window.removeEventListener('focus', this.resume);
  }

  // The speakingMode watcher fires a second SUSPEND; it is a no-op.
  // releaseAudio: real backgrounding only. A blur is not one: the mic
  // permission prompt, the YouTube iframe and devtools all blur the window,
  // and a released context stays silent until the next tap (BUGFIX-010).
  suspend ({ releaseAudio = false } = {}) {
    const { store, controller } = this.host;
    // The native share sheet blurs the window; suspending would discard the
    // feedback being shared. openShare() already released the mic.
    if (store.state.settings.showShareSheet) return;

    // Release capture, and the AudioContext iOS may silently stop rendering
    // through after backgrounding (BUGFIX-004 §1a).
    controller.closeMic();
    controller.stopSelfPlayback();
    if (releaseAudio) releaseAudioContext();
    if (ACTIVE_STATUSES.indexOf(this.host.getDrill().status) === -1) return;
    this._micWasOnBeforeSuspend = store.state.recognition.speakingMode;
    store.dispatch('recognition/setSpeakingMode', false);
    this.host.send({ type: 'SUSPEND' });
  }

  // Restores only the toggle, never capture, so iOS has nothing to object to.
  resume () {
    if (!this._micWasOnBeforeSuspend) return;
    this._micWasOnBeforeSuspend = false;
    if (micStream.isIOS()) return;
    if (this.host.store.state.recognition.micDenied) return;
    this.host.store.dispatch('recognition/setSpeakingMode', true);
  }
}
